import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import PageHeader from '../components/PageHeader';
import Card from '../components/Card';
import StatCard from '../components/StatCard';
import StatusBadge from '../components/StatusBadge';
import InlineForm from '../components/InlineForm';
import ConfirmDialog from '../components/ConfirmDialog';
import { Loading, ErrorState, EmptyState } from '../components/States';
import { number, relativeTime } from '../lib/format';
import { toId } from '../lib/ids';

// Identity & Access Management console, scoped to superadmins: manage tenant
// organizations and the users (and roles) that belong to them.
const ROLES = [
  { id: 'superadmin', label: 'Superadmin' },
  { id: 'org-admin', label: 'Org admin' },
  { id: 'member', label: 'Member' },
  { id: 'billing', label: 'Billing' },
];

const PLANS = ['Starter', 'Business', 'Enterprise'];
const ORG_STATUSES = ['operational', 'degraded', 'maintenance'];

const ROLE_TONE = {
  superadmin: 'danger',
  'org-admin': 'info',
  billing: 'warn',
  member: 'muted',
};

const ADMIN_ROLES = new Set(['superadmin', 'org-admin']);

const BLANK_ORG = { name: '', plan: 'Starter', seats: '', status: 'operational' };

// How a scheduled job's last outcome reads. Partial is its own tone rather than
// being folded into either neighbour: a sweep that stored nine groups out of ten
// is neither a success nor a failure, and the operator's next move is different
// from both.
const JOB_STATUS_TONE = {
  ok: 'ok',
  partial: 'warn',
  error: 'danger',
  cancelled: 'muted',
};

const JOB_STATUS_LABEL = {
  ok: 'Succeeded',
  partial: 'Partly failed',
  error: 'Failed',
  cancelled: 'Cancelled',
};

// While the sweep is running the page re-reads every few seconds, since the run
// finishes on the server with nothing to tell the browser about it.
const JOB_POLL_MS = 5000;

// "in 5h" for a scheduled time. Absent, unparseable, and past all read as '' —
// a next run in the past means the timer is not armed, and dating it would claim
// otherwise.
const untilTime = (iso) => {
  if (!iso) return '';
  const diff = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(diff) || diff <= 0) return '';
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'in under a minute';
  if (mins < 60) return `in ${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `in ${hours}h`;
  return `in ${Math.round(hours / 24)}d`;
};

// A run's duration, which is how an operator tells a sweep that did the work from
// one that gave up in the first second.
const durationText = (ms) => {
  if (!ms) return '';
  if (ms < 1000) return `${ms}ms`;
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  return `${mins}m ${secs % 60}s`;
};

const localTime = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

export default function Superadmin() {
  const orgs = useAsync(() => api.getOrganizations(), []);
  const users = useAsync(() => api.getUsers(), []);
  // Kept out of the page's own loading/error gate below: the scheduler is a
  // separate concern from IAM, and a job list that fails to load must cost its own
  // card rather than the organizations and users beside it.
  const jobs = useAsync(() => api.getJobs(), []);

  const [query, setQuery] = useState('');
  const [role, setRole] = useState('');
  const [busyId, setBusyId] = useState(null);

  // Organization create/edit happens in an inline form: `orgForm` holds the
  // draft, `orgEditing` is the id being edited (null while creating).
  const [orgForm, setOrgForm] = useState(null);
  const [orgEditing, setOrgEditing] = useState(null);
  const [orgSaving, setOrgSaving] = useState(false);
  const [orgError, setOrgError] = useState(null);

  // Invite form, also inline.
  const [userForm, setUserForm] = useState(null);
  const [userSaving, setUserSaving] = useState(false);
  const [userError, setUserError] = useState(null);

  // Scheduled jobs: which button on which row is mid-flight, and the last error
  // any of them returned.
  const [jobBusy, setJobBusy] = useState(null);
  const [jobError, setJobError] = useState(null);

  // Pending destructive action — the only thing that opens a modal.
  const [confirm, setConfirm] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState(null);

  const orgById = useMemo(
    () => Object.fromEntries((orgs.data || []).map((o) => [o.id, o])),
    [orgs.data]
  );

  // Users per organization, so we can show used-vs-licensed seats.
  const usersByOrg = useMemo(() => {
    const counts = {};
    for (const u of users.data || []) {
      counts[u.orgId] = (counts[u.orgId] || 0) + 1;
    }
    return counts;
  }, [users.data]);

  const stats = useMemo(() => {
    const all = users.data || [];
    return {
      orgs: (orgs.data || []).length,
      users: all.length,
      admins: all.filter((u) => ADMIN_ROLES.has(u.role)).length,
      invited: all.filter((u) => u.status === 'invited').length,
    };
  }, [orgs.data, users.data]);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (users.data || []).filter((u) => {
      if (role && u.role !== role) return false;
      if (!q) return true;
      return (
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        (orgById[u.orgId]?.name || '').toLowerCase().includes(q)
      );
    });
  }, [users.data, query, role, orgById]);

  // ---- Organizations ------------------------------------------------------

  const startCreateOrg = () => {
    setOrgEditing(null);
    setOrgError(null);
    setOrgForm({ ...BLANK_ORG });
  };

  const startEditOrg = (o) => {
    setOrgEditing(o.id);
    setOrgError(null);
    setOrgForm({
      name: o.name,
      plan: o.plan,
      seats: o.seats ? String(o.seats) : '',
      status: o.status,
    });
  };

  const closeOrgForm = () => {
    setOrgForm(null);
    setOrgEditing(null);
    setOrgError(null);
  };

  const updateOrgField = (key) => (e) =>
    setOrgForm((f) => ({ ...f, [key]: e.target.value }));

  const submitOrg = async (e) => {
    e.preventDefault();
    if (!orgForm.name.trim()) {
      setOrgError('Name is required');
      return;
    }
    const payload = {
      name: orgForm.name.trim(),
      plan: orgForm.plan,
      seats: parseInt(orgForm.seats, 10) || 0,
      status: orgForm.status,
    };
    setOrgSaving(true);
    setOrgError(null);
    try {
      if (orgEditing) await api.updateOrganization(orgEditing, payload);
      else await api.createOrganization(payload);
      closeOrgForm();
      orgs.reload();
    } catch (err) {
      setOrgError(err.message);
    } finally {
      setOrgSaving(false);
    }
  };

  // ---- Users --------------------------------------------------------------

  const startInvite = () => {
    setUserError(null);
    setUserForm({
      name: '',
      email: '',
      orgId: String((orgs.data || [])[0]?.id ?? ''),
      role: 'member',
    });
  };

  const updateUserField = (key) => (e) =>
    setUserForm((f) => ({ ...f, [key]: e.target.value }));

  const submitUser = async (e) => {
    e.preventDefault();
    setUserSaving(true);
    setUserError(null);
    try {
      await api.createUser({ ...userForm, orgId: toId(userForm.orgId) });
      setUserForm(null);
      users.reload();
    } catch (err) {
      setUserError(err.message);
    } finally {
      setUserSaving(false);
    }
  };

  const changeRole = async (id, nextRole) => {
    setBusyId(id);
    try {
      await api.updateUser(id, { role: nextRole });
      users.reload();
    } finally {
      setBusyId(null);
    }
  };

  // ---- Scheduled jobs -----------------------------------------------------

  const jobRows = jobs.data || [];
  const anyJobRunning = jobRows.some((j) => j.running);
  const jobReload = jobs.reload;

  // A run happens on the server and finishes there, so nothing tells the browser
  // about it. Polled only while something is running: an idle scheduler changes
  // once a day, and the row already says when.
  useEffect(() => {
    if (!anyJobRunning) return undefined;
    const timer = setInterval(jobReload, JOB_POLL_MS);
    return () => clearInterval(timer);
  }, [anyJobRunning, jobReload]);

  const jobAction = async (job, action, call) => {
    setJobBusy(`${job.id}:${action}`);
    setJobError(null);
    try {
      await call(job.id);
      jobReload();
    } catch (err) {
      setJobError(`${job.name}: ${err.message}`);
    } finally {
      setJobBusy(null);
    }
  };

  // ---- Destructive actions (the one modal) --------------------------------

  const runConfirm = async () => {
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      await confirm.run();
      setConfirm(null);
      confirm.after();
    } catch (err) {
      setConfirmError(err.message);
    } finally {
      setConfirmBusy(false);
    }
  };

  const askDeleteOrg = (o) =>
    setConfirm({
      title: 'Delete organization',
      message: `Delete "${o.name}"?`,
      detail:
        'An organization can only be deleted once it has no users. Any contracts held by its groups are unaffected.',
      confirmLabel: 'Delete organization',
      run: () => api.deleteOrganization(o.id),
      after: () => orgs.reload(),
    });

  const askDeleteUser = (u) =>
    setConfirm({
      title: 'Remove user',
      message: `Remove ${u.name} (${u.email})?`,
      detail: 'They lose access immediately and drop out of every user group.',
      confirmLabel: 'Remove user',
      run: () => api.deleteUser(u.id),
      after: () => users.reload(),
    });

  const loading = orgs.loading || users.loading;
  const error = orgs.error || users.error;

  if (loading) return <Loading label="Loading IAM…" />;
  if (error) {
    return (
      <ErrorState
        error={error}
        onRetry={() => {
          orgs.reload();
          users.reload();
        }}
      />
    );
  }

  return (
    <div className="stack">
      <PageHeader
        title="Identity & Access"
        subtitle="Manage organizations, users, and roles across the platform."
        actions={
          <button className="btn btn--primary" onClick={startInvite}>
            + Invite user
          </button>
        }
      />

      <div className="grid grid--kpi">
        <StatCard label="Organizations" value={number(stats.orgs)} hint="Active tenants" />
        <StatCard label="Users" value={number(stats.users)} hint="Across all orgs" />
        <StatCard label="Admins" value={number(stats.admins)} hint="Superadmin & org-admin" />
        <StatCard label="Pending invites" value={number(stats.invited)} hint="Awaiting acceptance" />
      </div>

      {/* Scheduled jobs. The console's only unattended writer: it stores each
          client group's usage on a timer, which is the same write a superadmin
          makes by hand with Store for client on Cost & Billing. Start/Stop are
          therefore consequential — a stopped job means every client's bill quietly
          stops moving — so the row says when it last ran and what it stored, not
          just whether it is on. */}
      <Card
        title="Scheduled jobs"
        action={
          <button
            className="btn btn--ghost btn--sm"
            onClick={jobReload}
            disabled={jobs.loading}
          >
            {jobs.loading ? 'Refreshing…' : 'Refresh'}
          </button>
        }
      >
        {jobs.loading && !jobs.data ? (
          <Loading label="Loading jobs…" />
        ) : jobs.error ? (
          <ErrorState error={jobs.error} onRetry={jobReload} />
        ) : jobRows.length === 0 ? (
          <EmptyState
            title="No scheduled jobs"
            detail="Jobs are defined in the backend; this build registers none."
          />
        ) : (
          <>
            {jobError ? <div className="form-error">{jobError}</div> : null}
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Job</th>
                    <th>Schedule</th>
                    <th>State</th>
                    <th>Last run</th>
                    <th aria-label="actions" />
                  </tr>
                </thead>
                <tbody>
                  {jobRows.map((j) => {
                    const busy = jobBusy?.startsWith(`${j.id}:`);
                    return (
                      <tr key={j.id}>
                        <td>
                          <div className="cell-primary">{j.name}</div>
                          <div className="cell-sub">{j.description}</div>
                        </td>
                        <td>
                          {j.schedule}
                          {/* Only meaningful while the job is armed: a stopped
                              job has no next run, and the backend omits it. */}
                          <div className="cell-sub">
                            {j.nextRunAt
                              ? `Next ${localTime(j.nextRunAt)}${
                                  untilTime(j.nextRunAt) ? ` · ${untilTime(j.nextRunAt)}` : ''
                                }`
                              : 'Not scheduled'}
                          </div>
                        </td>
                        <td>
                          {/* Running and armed are separate facts: a manual run
                              works on a stopped job, and a stop during a sweep
                              leaves it running for as long as the abort takes. */}
                          <span
                            className={`badge badge--${
                              j.running ? 'info' : j.enabled ? 'ok' : 'muted'
                            }`}
                          >
                            <span className="badge__dot" />
                            {j.running ? 'Running' : j.enabled ? 'Scheduled' : 'Stopped'}
                          </span>
                          <div className="cell-sub">
                            {number(j.runs)} run{j.runs === 1 ? '' : 's'}
                            {j.failures > 0 ? ` · ${number(j.failures)} failed` : ''}
                          </div>
                        </td>
                        <td>
                          {j.lastRunAt ? (
                            <>
                              <div className="cell-primary" title={localTime(j.lastRunAt)}>
                                {relativeTime(j.lastRunAt)}
                                {durationText(j.lastDurationMs)
                                  ? ` · ${durationText(j.lastDurationMs)}`
                                  : ''}
                              </div>
                              {j.lastStatus ? (
                                <span
                                  className={`badge badge--${
                                    JOB_STATUS_TONE[j.lastStatus] || 'muted'
                                  }`}
                                >
                                  <span className="badge__dot" />
                                  {JOB_STATUS_LABEL[j.lastStatus] || j.lastStatus}
                                </span>
                              ) : null}
                              {/* What the sweep stored, or why it did not. The
                                  whole point of the row: "it ran" and "it stored
                                  anything" are different claims. */}
                              {j.lastMessage ? (
                                <div className="cell-sub">{j.lastMessage}</div>
                              ) : null}
                            </>
                          ) : (
                            <span className="cell-sub">Never run</span>
                          )}
                        </td>
                        <td className="num">
                          <div className="row-actions">
                            <button
                              className="btn btn--ghost btn--sm"
                              disabled={busy || j.enabled}
                              onClick={() => jobAction(j, 'start', api.startJob)}
                              title="Arm the schedule"
                            >
                              Start
                            </button>
                            <button
                              className="btn btn--ghost btn--sm"
                              disabled={busy || !j.enabled}
                              onClick={() => jobAction(j, 'stop', api.stopJob)}
                              title="Disarm the schedule and abort the run in flight"
                            >
                              Stop
                            </button>
                            <button
                              className="btn btn--ghost btn--sm"
                              disabled={busy || !j.enabled}
                              onClick={() => jobAction(j, 'restart', api.restartJob)}
                              title="Abandon the run in flight and re-arm the timer from now"
                            >
                              Restart
                            </button>
                            <button
                              className="btn btn--primary btn--sm"
                              disabled={busy || j.running}
                              onClick={() => jobAction(j, 'run', api.runJob)}
                              title="Run once now, without changing the schedule"
                            >
                              {j.running ? 'Running…' : 'Run now'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="field__hint">
              A run stores usage for every user group linked to a tenancy — the same
              write as <strong>Store for client</strong> on Cost &amp; Billing, for all
              of them at once. Stopping a job leaves the last stored snapshot in place;
              it just stops moving.
            </p>
          </>
        )}
      </Card>

      <Card
        title="Organizations"
        action={
          <button className="btn btn--ghost btn--sm" onClick={startCreateOrg}>
            + New organization
          </button>
        }
      >
        {orgForm ? (
          <InlineForm
            title={orgEditing ? 'Edit organization' : 'New organization'}
            onSubmit={submitOrg}
            onCancel={closeOrgForm}
            submitting={orgSaving}
            submitLabel={orgEditing ? 'Save changes' : 'Create organization'}
            error={orgError}
          >
            <div className="form-grid">
              <label className="field">
                <span>Name</span>
                <input
                  className="input"
                  placeholder="e.g. Northwind Labs"
                  value={orgForm.name}
                  onChange={updateOrgField('name')}
                  autoFocus
                  required
                />
              </label>
              <label className="field">
                <span>Plan</span>
                <select className="input" value={orgForm.plan} onChange={updateOrgField('plan')}>
                  {PLANS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Licensed seats</span>
                <input
                  className="input"
                  type="number"
                  min="0"
                  placeholder="0 = not set"
                  value={orgForm.seats}
                  onChange={updateOrgField('seats')}
                />
              </label>
              <label className="field">
                <span>Status</span>
                <select className="input" value={orgForm.status} onChange={updateOrgField('status')}>
                  {ORG_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
            </div>
          </InlineForm>
        ) : null}

        {(orgs.data || []).length === 0 ? (
          <EmptyState
            title="No organizations"
            detail="Create one to start assigning users to it."
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Plan</th>
                  <th className="num">Seats used</th>
                  <th>Status</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {(orgs.data || []).map((o) => (
                  <tr key={o.id}>
                    <td>
                      <div className="cell-primary">{o.name}</div>
                      <div className="cell-sub mono">{o.id}</div>
                    </td>
                    <td>{o.plan}</td>
                    <td className="num">
                      {number(usersByOrg[o.id] || 0)} / {o.seats ? number(o.seats) : '—'}
                    </td>
                    <td><StatusBadge status={o.status} /></td>
                    <td className="num">
                      <div className="row-actions">
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => startEditOrg(o)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn--danger-ghost btn--sm"
                          onClick={() => askDeleteOrg(o)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card
        title={`Users & roles (${filteredUsers.length})`}
        action={
          <input
            className="input search"
            placeholder="Search name, email, org…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        }
      >
        {userForm ? (
          <InlineForm
            title="Invite user"
            onSubmit={submitUser}
            onCancel={() => setUserForm(null)}
            submitting={userSaving}
            submitLabel="Send invite"
            error={userError}
          >
            <div className="form-grid">
              <label className="field">
                <span>Name</span>
                <input
                  className="input"
                  placeholder="e.g. Jordan Lee"
                  value={userForm.name}
                  onChange={updateUserField('name')}
                  autoFocus
                  required
                />
              </label>
              <label className="field">
                <span>Email</span>
                <input
                  className="input"
                  type="email"
                  placeholder="e.g. jordan@company.com"
                  value={userForm.email}
                  onChange={updateUserField('email')}
                  required
                />
              </label>
              <label className="field">
                <span>Organization</span>
                <select className="input" value={userForm.orgId} onChange={updateUserField('orgId')}>
                  {(orgs.data || []).map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Role</span>
                <select className="input" value={userForm.role} onChange={updateUserField('role')}>
                  {ROLES.map((r) => (
                    <option key={r.id} value={r.id}>{r.label}</option>
                  ))}
                </select>
              </label>
            </div>
          </InlineForm>
        ) : null}

        <div className="filter-row" style={{ marginBottom: 16 }}>
          <label className="field">
            <span>Role</span>
            <select className="input" value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="">All roles</option>
              {ROLES.map((r) => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
          </label>
        </div>

        {filteredUsers.length === 0 ? (
          <EmptyState title="No users match" detail="Try clearing the filters or invite a user." />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Organization</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last active</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="cell-primary">{u.name}</div>
                      <div className="cell-sub mono">{u.email}</div>
                    </td>
                    <td>{orgById[u.orgId]?.name || u.orgId}</td>
                    <td>
                      <span className={`badge badge--${ROLE_TONE[u.role] || 'muted'}`}>
                        <span className="badge__dot" />
                        {u.role}
                      </span>
                    </td>
                    <td><StatusBadge status={u.status} /></td>
                    <td>{relativeTime(u.lastActive)}</td>
                    <td className="num">
                      <div className="row-actions">
                        <select
                          className="input input--sm"
                          value={u.role}
                          disabled={busyId === u.id}
                          onChange={(e) => changeRole(u.id, e.target.value)}
                          aria-label={`Change role for ${u.name}`}
                        >
                          {ROLES.map((r) => (
                            <option key={r.id} value={r.id}>{r.label}</option>
                          ))}
                        </select>
                        <button
                          className="btn btn--danger-ghost btn--sm"
                          onClick={() => askDeleteUser(u)}
                          disabled={busyId === u.id}
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {confirm ? (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          detail={confirm.detail}
          confirmLabel={confirm.confirmLabel}
          busy={confirmBusy}
          error={confirmError}
          onConfirm={runConfirm}
          onCancel={() => {
            setConfirm(null);
            setConfirmError(null);
          }}
        />
      ) : null}
    </div>
  );
}
